namespace advisorop.services {
    "use strict";

    // The advisor dashboard, the servicer dashboard and the household dashboard each list
    // Salesforce cases. They differ only in which cases they ask for - the columns, the status
    // selector, the detail popup and the "Add case" popup are the same everywhere, and are kept
    // here so three widgets cannot drift into three different case lists.
    export class SalesforceCaseGrid {

        // Salesforce is queried per status rather than the grid being filtered on screen, so
        // what is shown is always the whole set for that status. Open is the default everywhere.
        readonly statusOptions = [
            { value: "open", label: "Open cases" },
            { value: "closed", label: "Closed cases" },
            { value: "all", label: "All cases" }
        ];

        static $inject = ["$uibModal"];
        constructor(private $uibModal: angular.ui.bootstrap.IModalService) {
        }

        // dataSourceExpression is the controller-relative binding path the grid reads its rows
        // from, e.g. "vm.cases".
        buildGrid(dataSourceExpression: string,
            onCaseClick: (salesforceCase: domains.ISalesforceCaseTrackerData) => void)
            : DevExpress.ui.dxDataGridOptions {

            return {
                bindingOptions: {
                    dataSource: dataSourceExpression
                },
                paging: { pageSize: 8 },
                // showInfo would add a "Page 1 of 3 (24 items)" line under the grid.
                pager: { showInfo: false, visible: true },
                rowAlternationEnabled: true,
                showBorders: false,
                showColumnLines: false,
                columnAutoWidth: true,
                allowColumnReordering: false,
                allowColumnResizing: true,
                hoverStateEnabled: true,
                columnChooser: { enabled: false },
                filterRow: { visible: false },
                headerFilter: { visible: false },
                searchPanel: {
                    visible: true,
                    width: 200,
                    placeholder: "Search cases..."
                },
                columns: [
                    { dataField: "casenumber", caption: "Case #", width: 80 },
                    { dataField: "clientName", caption: "Client" },
                    { dataField: "accountName", caption: "Account" },
                    { dataField: "recordType", caption: "Type", width: 130 },
                    { dataField: "status", caption: "Status", width: 110 },
                    { dataField: "priority", caption: "Priority", width: 90 },
                    { dataField: "advisor", caption: "Advisor", width: 130 },
                    { dataField: "caseOwner", caption: "Case Owner", width: 130 },
                    {
                        dataField: "funding_Amount__c", caption: "Funding Amount", width: 120,
                        format: { type: "currency", precision: 0 }, alignment: "right"
                    },
                    {
                        dataField: "createdDate", caption: "Opened", width: 110,
                        dataType: "date", format: "MM/dd/yyyy"
                    }
                ],
                onRowClick: (e) => {
                    if (e.rowType !== "data") return;
                    onCaseClick(e.data as domains.ISalesforceCaseTrackerData);
                }
            };
        }

        // containerSelector is the widget's own root element, so the spinner covers that list
        // rather than the whole page.
        buildLoadPanel(containerSelector: string): DevExpress.ui.dxLoadPanelOptions {
            return {
                shadingColor: "rgba(0,0,0,0.4)",
                position: { of: containerSelector },
                showIndicator: true,
                showPane: true,
                shading: false,
                closeOnOutsideClick: false,
                message: "Loading cases from Salesforce...",
                bindingOptions: {
                    visible: "vm.loading"
                }
            };
        }

        openCaseDetail(salesforceCase: domains.ISalesforceCaseTrackerData, instanceUrl: string): void {
            this.$uibModal.open({
                animation: true,
                component: "popSalesforceCase",
                size: "lg",
                controllerAs: "vvm",
                bindToController: true,
                resolve: {
                    salesforceCase: () => salesforceCase,
                    instanceUrl: () => instanceUrl
                }
            });
        }

        // Opens the "Add case" form. Everything it offers is read from Salesforce when the modal
        // loads, so a case type or picklist changed in the org shows up without a release on our
        // side. apiScope decides which controller it talks to; household prefills the Household
        // lookup for a case opened from a household dashboard.
        //
        // Resolves when a case was created, rejects when the modal was dismissed.
        openAddCase(apiScope: SalesforceCaseScope = "advisor", household: domains.ISalesforceLookupResult = null): ng.IPromise<any> {
            return this.$uibModal.open({
                animation: true,
                component: "popSalesforceCaseNew",
                size: "lg",
                controllerAs: "vvm",
                bindToController: true,
                backdrop: "static",
                // The form is tall, so it sits at the top of the window instead of being
                // centred and pushed down.
                windowClass: "sf-case-modal-top",
                resolve: {
                    apiScope: () => apiScope,
                    household: () => household
                }
            }).result;
        }
    }
    angular.module(Constants.ADVOP_SERVICES).service("salesforceCaseGrid", SalesforceCaseGrid);
}
